import { useCallback, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { Dot, Star } from 'react-bootstrap-icons';
import { Link, Navigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import HTMLEllipsis from 'react-lines-ellipsis/lib/html';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';

import { DocumentModel } from '@/lib/models';
import { blogsService } from '@/lib/services';
import { useAccount, useCopyTextToClipboard } from '@/hooks';
import { AspectRatioLockedImage, AsyncPage, Avatar } from '@/components';

const ResponsiveEllipsis = responsiveHOC()(HTMLEllipsis);

export const BlogPostDisplayPublic = () => {
	const { account } = useAccount();
	const { copyTextToClipboard } = useCopyTextToClipboard();
	const { blogId } = useParams<{ blogId?: string }>();
	const [blog, setBlog] = useState<DocumentModel>();
	const [blogList, setBlogList] = useState<DocumentModel[]>([]);

	const fetchData = useCallback(async () => {
		if (!blogId) {
			throw new Error('blogId is undefined.');
		}

		const [blogResponse, blogListResponse] = await Promise.all([
			blogsService.getPublicBlog(blogId).fetch(),
			blogsService.getPublicBlogs({ size: 7, page: 0 }).fetch(),
		]);

		const filteredBlogList = blogListResponse.results.filter((b) => b.documentId !== blogResponse.blog.documentId);
		if (filteredBlogList.length > 6) {
			filteredBlogList.pop();
		}

		setBlog(blogResponse.blog);
		setBlogList(filteredBlogList);
	}, [blogId]);

	const handleShareButtonClick = () => {
		copyTextToClipboard(window.location.href, 'Link copied to clipboard.');
	};

	if (account && blogId) {
		return <Navigate to={`/research/${blogId}`} replace />;
	}

	return (
		<AsyncPage fetchData={fetchData}>
			<Container className="pt-11">
				<Row className="mb-5">
					<Col>
						<h5 className="mb-3 font-weight-regular text-muted">Blog Post</h5>
						<h1 className="mb-0">{blog?.name}</h1>
						{(blog?.authors ?? []).length > 0 && (
							<div className="mt-5">
								{blog?.authors.map((author, authorIndex) => {
									const isLast = blog?.authors.length - 1 === authorIndex;

									return (
										<div
											className={classNames('d-inline-flex align-items-center', {
												'mr-2': !isLast,
											})}
										>
											<Avatar size={30} url={author.imageUrl} />
											<strong className="ml-2 small">{author.name}</strong>
										</div>
									);
								})}
							</div>
						)}
					</Col>
					<Col xl={5}>
						<AspectRatioLockedImage width={16} height={9} src={blog?.image?.viewUrl} />
					</Col>
				</Row>
				<Row className="mb-8 py-3 border-top border-bottom align-items-center">
					<Col>
						<div className="d-flex align-items-center">
							<h5 className="mb-0 font-weight-regular text-muted">
								Published {blog?.publicationDateDescription}
							</h5>
							{blog?.rating?.blogRatingId && (
								<div className="d-inline-flex align-items-center text-muted">
									<Dot className="mx-1" />
									<Star width={20} height={20} className="mr-1 text-primary" />
									<h5 className="mb-0">
										<strong>{blog.rating.averageRating.toFixed(1)}</strong>
									</h5>
								</div>
							)}
						</div>
					</Col>
					<Col>
						<div className="d-flex align-items-center justify-content-end">
							<Button variant="outline-primary" size="sm" onClick={handleShareButtonClick}>
								Share
							</Button>
						</div>
					</Col>
				</Row>
				<Row>
					<Col xs={12} xl={7}>
						<div className="mb-10 html-editor" dangerouslySetInnerHTML={{ __html: blog?.content ?? '' }} />
						<hr className="mb-5 pdf-ignore" />
						{blog?.ctaButtonText && blog?.ctaButtonUrl && (
							<Button
								className="text-nowrap mr-2"
								variant="secondary"
								size="sm"
								href={blog?.ctaButtonUrl}
								target="_blank"
								rel="noopener noreferrer"
							>
								{blog?.ctaButtonText}
							</Button>
						)}
						<Button
							className="text-nowrap mb-10"
							variant="primary"
							size="sm"
							href="https://www.thehackettgroup.com/contact"
							target="_blank"
							rel="noopener noreferrer"
						>
							Talk with our Experts
						</Button>
					</Col>
					<Col xs={12} xl={{ span: 4, offset: 1 }}>
						<div className="bg-white border p-6">
							<div className="d-flex align-items-center justify-content-between">
								<h4 className="mb-0 font-weight-bold">More Blog Posts</h4>
								<Link to="/public/blogs">View All</Link>
							</div>
							{blogList.map((blogListItem, blogListItemIndex) => {
								const isLast = blogList.length - 1 === blogListItemIndex;
								return (
									<>
										<div
											className={classNames('pt-5', {
												'pb-5': !isLast,
												'pb-0': isLast,
											})}
										>
											<Link
												to={`/public/blogs/${blogListItem.documentId}`}
												className="d-block mb-2 font-weight-bold text-truncate"
											>
												{blogListItem.name}
											</Link>
											<ResponsiveEllipsis
												className="html-content"
												unsafeHTML={blogListItem.contentDescription ?? ''}
												maxLine="2"
												component="p"
											/>
											<p className="mb-0 small text-uppercase text-muted font-weight-semi-bold">
												{blogListItem.publicationDateDescription}
											</p>
										</div>
										{!isLast && <hr />}
									</>
								);
							})}
						</div>
					</Col>
				</Row>
			</Container>
		</AsyncPage>
	);
};
